import { FC, useState } from 'react';
import { Modal, Input, Form, Typography } from 'antd';

const { Text } = Typography;

interface DuplicateFormModalProps {
    open: boolean;
    onConfirm: (newName: string) => Promise<void>;
    onCancel: () => void;
    currentName: string;
    type: 'form' | 'entity' | 'compositeField';
}

/**
 * Modal component for duplicating a form or entity type.
 * Allows user to provide a new name for the duplicated structure.
 */
export const DuplicateFormModal: FC<DuplicateFormModalProps> = ({
    open,
    onConfirm,
    onCancel,
    currentName,
    type
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            await onConfirm(values.name);
            form.resetFields();
            setLoading(false);
        } catch (error) {
            console.error('Validation failed:', error);
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={`Duplicate ${type === 'form' ? 'Form' : type === 'entity' ? 'Record' : 'Composite Field'} Type`}
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            okText="Duplicate"
            cancelText="Cancel"
            confirmLoading={loading}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ name: `${currentName} (Copy)` }}
            >
                <Form.Item
                    name="name"
                    label="Name for the duplicated type"
                    rules={[
                        { required: true, message: 'Please enter a name' },
                        { min: 1, message: 'Name must not be empty' },
                        { max: 100, message: 'Name must be less than 100 characters' }
                    ]}
                >
                    <Input
                        placeholder={`Enter name for the new ${type} type`}
                        autoFocus
                    />
                </Form.Item>
            </Form>
            <Text type="secondary">
                This will create a new {type} type with the same structure and configuration as "{currentName}".
            </Text>
        </Modal>
    );
};